import { supabase } from "./supabase";
import { Tables, TablesInsert, TablesUpdate } from "@/types/database";

export type Label = Tables<"labels">;

// Centralized label cache for use across React and non-React code (e.g., Zustand stores)
const labelCache = new Map<string, { data: Label | null; timestamp: number }>();
const LABEL_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const pendingLabelRequests = new Map<string, Promise<Label | null>>();

/**
 * LabelCacheService provides cached access to user label data.
 * Use this in Zustand stores or other non-React code.
 * React components should use useLabel() from LabelProvider instead.
 *
 * NOTE: Not all users have a label. The service gracefully returns null
 * when a user has no label_tag or their label doesn't exist.
 */
export class LabelCacheService {
  /**
   * Get the user's label with caching.
   * @returns Label object or null if user has no label
   */
  static async getLabel(userId: string): Promise<Label | null> {
    // Check cache first
    const cached = labelCache.get(userId);
    if (cached && Date.now() - cached.timestamp < LABEL_CACHE_DURATION) {
      return cached.data;
    }

    // Check if request is already pending (deduplication)
    if (pendingLabelRequests.has(userId)) {
      return pendingLabelRequests.get(userId)!;
    }

    const fetchPromise = (async () => {
      try {
        const result = await getUserLabel(userId);
        // getUserLabel returns undefined data when user has no label - normalize to null
        const labelData = result.success && result.data ? result.data : null;

        // Cache the result (including null for users without labels)
        labelCache.set(userId, { data: labelData, timestamp: Date.now() });
        return labelData;
      } finally {
        pendingLabelRequests.delete(userId);
      }
    })();

    pendingLabelRequests.set(userId, fetchPromise);
    return fetchPromise;
  }

  /**
   * Get just the label_tag (label ID) for a user.
   * @returns label_tag string or null if user has no label
   */
  static async getLabelTag(userId: string): Promise<string | null> {
    const label = await this.getLabel(userId);
    return label?.id ?? null;
  }

  /**
   * Clear cached label data for a user or all users.
   */
  static clearCache(userId?: string): void {
    if (userId) {
      labelCache.delete(userId);
      pendingLabelRequests.delete(userId);
    } else {
      labelCache.clear();
      pendingLabelRequests.clear();
    }
  }
}
export type LabelInsert = TablesInsert<"labels">;
export type LabelUpdate = TablesUpdate<"labels">;

// Derive a vanity hostname prefix from a label name (first word, alphanumeric only)
export function labelHostnamePrefix(name: string): string {
  if (!name) return "";
  const firstWord = name.trim().split(/\s+/)[0] ?? "";
  return firstWord.replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
}

// Generate label ID from name (uppercase with spaces as underscores)
export function generateLabelId(name: string): string {
  // Uppercase, replace non-alphanumeric with underscores, collapse repeats, trim
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Create a new label (admin only)
export async function createLabel(
  labelData: Omit<LabelInsert, "id"> & { organization_id?: string }
): Promise<{ success: boolean; data?: Label; error?: string }> {
  try {
    const labelId = generateLabelId(labelData.name);

    // Build insert payload - only include optional fields if provided
    const insertPayload: any = {
      name: labelData.name,
      id: labelId,
    };

    if (labelData.cover_image) {
      insertPayload.cover_image = labelData.cover_image;
    }

    if (labelData.organization_id) {
      insertPayload.organization_id = labelData.organization_id;
    }

    const { data, error } = await supabase
      .from("labels")
      .insert(insertPayload)
      .select()
      .single();

    if (error) {
      // Surface more detailed error info to aid debugging (code/details if available)
      const detailedMessage = [
        error.message,
        (error as any).code,
        (error as any).details,
        (error as any).hint,
      ]
        .filter(Boolean)
        .join(" | ");
      return { success: false, error: detailedMessage };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: "Failed to create label" };
  }
}

// Upload a label cover image (expects a data URL from cropper) and return a public URL
export async function uploadLabelCover(
  croppedDataUrl: string
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    if (!croppedDataUrl || !croppedDataUrl.startsWith("data:")) {
      return { success: false, error: "Invalid data URL format" };
    }

    // Convert data URL to Blob
    let blob: Blob;
    try {
      const response = await fetch(croppedDataUrl);
      blob = await response.blob();
    } catch (fetchErr: any) {
      return {
        success: false,
        error: `Blob conversion failed: ${fetchErr.message}`,
      };
    }

    const fileExt = (blob.type && blob.type.split("/")[1]) || "png";
    const fileName = `labels/label-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("profile-images")
      .upload(fileName, blob, {
        contentType: blob.type || "image/png",
        upsert: true,
      });

    if (uploadError) {
      const detailed = [uploadError.message, (uploadError as any)?.code]
        .filter(Boolean)
        .join(" | ");
      return { success: false, error: detailed };
    }

    const { data } = supabase.storage
      .from("profile-images")
      .getPublicUrl(fileName);
    const publicUrl = data?.publicUrl;
    if (!publicUrl) {
      return { success: false, error: "Failed to get public URL" };
    }

    return { success: true, url: publicUrl };
  } catch (err: any) {
    return { success: false, error: err?.message || "Upload failed" };
  }
}

// Get all labels
export async function getAllLabels(): Promise<{
  success: boolean;
  data?: Label[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from("labels")
      .select("*")
      .order("name");

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: "Failed to fetch labels" };
  }
}

/** A label's `cover_image` (its logo) when it is a usable image source — an http(s) or data: URL — else null. */
export function labelImageUrl(cover: string | null | undefined): string | null {
  return cover && (/^https?:\/\//i.test(cover) || cover.startsWith("data:")) ? cover : null;
}

// Cache + deduplication for getLabelById (prevents N+1 when many TrackCards share a label)
const labelByIdCache = new Map<string, { data: Label; timestamp: number }>();
const pendingLabelByIdRequests = new Map<string, Promise<{ success: boolean; data?: Label; error?: string }>>();

/**
 * The label `getLabelById` last loaded, read synchronously, however old; undefined when it never
 * has. A component starts from it so a logo already fetched is in its first paint instead of
 * arriving after an effect — also when a page comes back after the five-minute cache has lapsed
 * (the caller's effect still asks `getLabelById`, which reads it again).
 */
export function peekLabelById(labelId: string): Label | undefined {
  return labelByIdCache.get(labelId)?.data;
}

// Get label by ID (cached, deduplicated)
export async function getLabelById(
  labelId: string
): Promise<{ success: boolean; data?: Label; error?: string }> {
  // Check cache
  const cached = labelByIdCache.get(labelId);
  if (cached && Date.now() - cached.timestamp < LABEL_CACHE_DURATION) {
    return { success: true, data: cached.data };
  }

  // Deduplicate in-flight requests
  if (pendingLabelByIdRequests.has(labelId)) {
    return pendingLabelByIdRequests.get(labelId)!;
  }

  const fetchPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from("labels")
        .select("*")
        .eq("id", labelId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      if (data) {
        labelByIdCache.set(labelId, { data, timestamp: Date.now() });
      }
      return { success: true, data: data ?? undefined };
    } catch (error) {
      return { success: false, error: "Failed to fetch label" };
    } finally {
      pendingLabelByIdRequests.delete(labelId);
    }
  })();

  pendingLabelByIdRequests.set(labelId, fetchPromise);
  return fetchPromise;
}

// Update label (admin only)
export async function updateLabel(
  labelId: string,
  updates: LabelUpdate
): Promise<{ success: boolean; data?: Label; error?: string }> {
  try {
    const { data, error } = await supabase
      .from("labels")
      .update(updates)
      .eq("id", labelId)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: "Failed to update label" };
  }
}

// Delete label (admin only)
export async function deleteLabel(
  labelId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.from("labels").delete().eq("id", labelId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: "Failed to delete label" };
  }
}

// Get users associated with a label
export async function getLabelUsers(
  labelId: string
): Promise<{ success: boolean; data?: any[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("user_id, email, role, created_at")
      .eq("label_tag", labelId)
      .order("created_at");

    if (error) {
      return { success: false, error: error.message };
    }

    // Map user_id → id for consumers that expect an `id` field
    const mapped = (data || []).map((d: any) => ({ ...d, id: d.user_id }));
    return { success: true, data: mapped };
  } catch (error) {
    return { success: false, error: "Failed to fetch label users" };
  }
}

// Remove user from label.
// label_tag is a locked authority column (P2b Task 13 profiles lockdown) — the caller can no
// longer clear it (or reset the consumed invite code, which is admin-RLS-locked) via direct
// client writes. It is cleared server-side by the `leave_label` SECURITY DEFINER RPC, which
// clears the AUTHENTICATED caller's OWN label_tag and resets the invite code they consumed —
// atomically. The legacy `userId` arg is no longer used (identity comes from auth.uid(), so a
// user can only remove themselves), matching the redeem_label_invite_code pattern.
export async function removeUserFromLabel(
  _userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.rpc("leave_label");
    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: "Failed to remove from label" };
  }
}

// Get user's label information
export async function getUserLabel(
  userId: string
): Promise<{ success: boolean; data?: Label; error?: string }> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select(
        `
        label_tag,
        labels!inner(
          id,
          name,
          cover_image,
          created_at,
          updated_at
        )
      `
      )
      .eq("user_id", userId)
      .not("label_tag", "is", null)
      .maybeSingle();

    if (error) {
      return { success: false, error: error.message };
    }

    // If no data or no label_tag, return success with no data
    if (!data || !data.labels) {
      return { success: true, data: undefined };
    }

    const labelData = {
      ...(data.labels as unknown as Label),
      acr_identify_enabled: false,
    };

    // Keep the label fetch backward-compatible while the additive ACR migration is
    // prepared but not yet applied. If the column is missing, uploads remain usable
    // with the ACR toggle disabled.
    try {
      const { data: acrFlag } = await supabase
        .from("labels")
        .select("acr_identify_enabled")
        .eq("id", labelData.id)
        .maybeSingle();
      if (typeof acrFlag?.acr_identify_enabled === "boolean") {
        labelData.acr_identify_enabled = acrFlag.acr_identify_enabled;
      }
    } catch (err: any) {
      // 42703 = undefined_column. That was expected before the additive ACR
      // migration existed; other failures should be visible while defaulting off.
      if (err?.code !== "42703") {
        console.warn("[labels] failed to read acr_identify_enabled, defaulting false", err);
      }
    }

    return { success: true, data: labelData };
  } catch (error) {
    return { success: false, error: "Failed to fetch user label" };
  }
}
